import { useCallback, useEffect, useState } from 'react'

const useFlyout = (id: string, isOpen: boolean, onClose: () => void): void => {
	const [flyout, setFlyout] = useState<Element>(null)
	const [flyoutButton, setFlyoutButton] = useState<Element>(null)

	const isButtonOrFlyout = (id: string, target: HTMLInputElement): boolean => {
		if (target.closest(`[aria-controls="${id}"]`) || target.closest(`#${id}`)) {
			return true
		}
		return false
	}

	const closeOnOutsideClick = useCallback(
		(event) => {
			if (!isButtonOrFlyout(id, event.target)) {
				onClose()
			}
		},
		[id, onClose]
	)

	const closeOnEscapeKey = useCallback(
		(event) => {
			if (event.key === 'Escape') {
				onClose()
				;(flyoutButton as HTMLElement)?.focus()
			}
		},
		[flyoutButton, onClose]
	)

	useEffect(() => {
		let timeoutId
		if (isOpen) {
			timeoutId = setTimeout(() => {
				window?.addEventListener('click', closeOnOutsideClick)
			})
			flyout?.addEventListener('keydown', closeOnEscapeKey)
			flyoutButton?.addEventListener('keydown', closeOnEscapeKey)
		}
		return () => {
			clearTimeout(timeoutId)
			window.removeEventListener('click', closeOnOutsideClick)
			flyout?.removeEventListener('keydown', closeOnEscapeKey)
			flyoutButton?.removeEventListener('keydown', closeOnEscapeKey)
		}
	}, [closeOnEscapeKey, closeOnOutsideClick, flyout, flyoutButton, isOpen])

	useEffect(() => {
		setFlyout(document.getElementById(id))
		setFlyoutButton(document.querySelector(`[aria-controls="${id}"]`))
		return () => {
			setFlyout(null)
			setFlyoutButton(null)
		}
	}, [id])
}

export default useFlyout
